/**
 * Zustand auth store.
 *
 * Holds:
 *   - accessToken in memory only (cleared on tab close)
 *   - refreshToken in localStorage, so the user stays logged in across reloads
 *   - the current user profile (from /auth/me)
 *   - the user's effective permissions (from /auth/me/permissions)
 *   - the user's navigation tree (from /auth/me/navigation)
 *   - the active client_id (super-admins use this to act inside one tenant)
 *
 * NOTE on security: storing refresh tokens in localStorage is acceptable for
 * a local/dev setup. For production, we should switch to an httpOnly cookie
 * issued by the backend. That migration is tracked in the deployment phase.
 */
import { create } from 'zustand';

const REFRESH_KEY = 'aip.refresh';
const ACTIVE_CLIENT_KEY = 'aip.activeClient';

function loadRefresh() {
  try { return localStorage.getItem(REFRESH_KEY) || null; } catch { return null; }
}
function loadActiveClient() {
  try { return localStorage.getItem(ACTIVE_CLIENT_KEY) || null; } catch { return null; }
}
function saveRefresh(token) {
  try {
    if (token) localStorage.setItem(REFRESH_KEY, token);
    else localStorage.removeItem(REFRESH_KEY);
  } catch {}
}
function saveActiveClient(id) {
  try {
    if (id) localStorage.setItem(ACTIVE_CLIENT_KEY, id);
    else localStorage.removeItem(ACTIVE_CLIENT_KEY);
  } catch {}
}

export const useAuthStore = create((set, get) => ({
  accessToken: null,
  refreshToken: loadRefresh(),
  expiresAt: 0,                 // ms epoch
  user: null,                   // /auth/me payload
  permissions: null,            // /auth/me/permissions payload
  navigation: null,             // /auth/me/navigation payload
  activeClientId: loadActiveClient(),
  status: 'idle',               // idle | bootstrapping | authed | guest

  setTokens({ accessToken, refreshToken, expiresIn }) {
    saveRefresh(refreshToken);
    set({
      accessToken,
      refreshToken,
      expiresAt: Date.now() + (Number(expiresIn) || 0) * 1000,
    });
  },

  setUser(user) {
    set({ user });
    // Choose a sensible default active client for non-super-admins
    const current = get().activeClientId;
    if (!current && user?.primary_client_id) {
      saveActiveClient(user.primary_client_id);
      set({ activeClientId: user.primary_client_id });
    }
  },

  setPermissions(permissions) { set({ permissions }); },
  setNavigation(navigation)   { set({ navigation }); },

  setActiveClientId(clientId) {
    saveActiveClient(clientId);
    set({ activeClientId: clientId || null });
  },

  setStatus(status) { set({ status }); },

  clear() {
    saveRefresh(null);
    saveActiveClient(null);
    set({
      accessToken: null,
      refreshToken: null,
      expiresAt: 0,
      user: null,
      permissions: null,
      navigation: null,
      activeClientId: null,
      status: 'guest',
    });
  },
}));

/** Convenience selectors */
export const isAuthenticated = (s) => Boolean(s.accessToken && s.user);
export const can = (s, perm, scope = {}) => {
  const p = s.permissions;
  if (!p) return false;
  if (p.is_super_admin) return true;
  if (p.permissions?.includes(perm)) return true;
  if (scope.clientId && p.client_permissions?.[scope.clientId]?.includes(perm)) return true;
  if (scope.projectId && p.project_permissions?.[scope.projectId]?.includes(perm)) return true;
  return false;
};
